import { allCountries } from './countries';

const MAX_VISIBLE_COUNTRIES = 3;

type DealershipGroupLike = {
    title: string;
    countries: string[];
};

export type CountryChip = {
    code: string;
    name: string;
};

export type DealershipGroupLabel = {
    label: string;
    tooltip?: string;
    isCountryGroup: boolean;
    countries: CountryChip[];
    overflowCount: number;
    overflowCountries: CountryChip[];
    overflowTooltip?: string;
};

const getCountryCode = (countryIri: string) => countryIri.split('/').pop() || '';

const getCountryName = (countryIri: string) =>
    allCountries.find(country => country['@id'] === countryIri)?.name ||
    getCountryCode(countryIri);

/**
 * Country-based dealership groups are displayed as "Country DEU, CHE" rather than
 * their raw title (e.g. "Germany Audi Country Group"), truncated to 3 codes with
 * a "+N" suffix and the full country names available via tooltip. Visible/overflow
 * country chips are also exposed so callers can render them individually.
 */
export const getDealershipGroupLabel = (
    group: DealershipGroupLike,
    countryPrefix: string
): DealershipGroupLabel => {
    if (!group.countries || group.countries.length === 0) {
        return {
            label: group.title,
            isCountryGroup: false,
            countries: [],
            overflowCount: 0,
            overflowCountries: []
        };
    }

    const allChips = group.countries.map(iri => ({
        code: getCountryCode(iri),
        name: getCountryName(iri)
    }));

    const visibleChips = allChips.slice(0, MAX_VISIBLE_COUNTRIES);
    const overflowChips = allChips.slice(MAX_VISIBLE_COUNTRIES);

    const label = `${countryPrefix} ${visibleChips.map(chip => chip.code).join(', ')}${
        overflowChips.length > 0 ? ` +${overflowChips.length}` : ''
    }`;

    return {
        label,
        tooltip: allChips.map(chip => chip.name).join(', '),
        isCountryGroup: true,
        countries: visibleChips,
        overflowCount: overflowChips.length,
        overflowCountries: overflowChips,
        overflowTooltip:
            overflowChips.length > 0
                ? overflowChips.map(chip => chip.name).join(', ')
                : undefined
    };
};
